import { T, useGT } from 'gt-react';
import { TrashIcon } from '@phosphor-icons/react';

import Page from 'src/components/page/Page';
import { Button } from '@/components/ui/button';
import {
  DAILY_REPORT_ACCEPT,
  validateDailyReportFile,
} from '@/lib/daily-reports/excelFile';
import { FileDropzone } from '../../components/FileDropzone';
import { DailyReportsDownloadControls } from './DailyReportsDownloadControls';
import {
  AllClearPanel,
  AnalysisSummary,
  FileResultList,
  FlaggedRowsTable,
} from './DailyReportsReadout';
import {
  RdStockMappedTable,
  RdStockUnmatchedTable,
  SupersededMappedTable,
  SupersededUnmatchedTable,
} from './RdStockReadout';
import {
  PotfUpcTable,
  VendorItemListTable,
  WebNoPriceTable,
} from './WorklistReadout';
import { useDailyReports } from './useDailyReports';

/**
 * Item Master → Utilities → Daily Reports: drop the AS/400 Excel emails and
 * see Suspended Item, STOCK_V, STOCKS_V, WEBNOPRF, POTF_UPC, and VITMLISTER
 * discrepancies.
 */
export function DailyReportsPage() {
  const gt = useGT();
  const {
    results,
    summary,
    pending,
    isAnalyzing,
    addFiles,
    removeFile,
    clear,
  } = useDailyReports();
  const hasAnalyzed = summary.analyzedCount > 0;
  const hasAnalyzedSuspended = results.some(
    (result) => result.status === 'analyzed' && result.kind === 'suspendedItem',
  );

  return (
    <Page
      title={
        <T context="page title for the daily reports analyzer utility">
          Daily Reports
        </T>
      }
      subtitle={
        <T context="subtitle for the daily reports analyzer utility">
          Drop the daily AS/400 Excel reports. Suspended Item, RD stock
          (STOCK_V), superseded-with-stock (STOCKS_V), web-no-price (WEBNOPRF),
          POTF UPC, and vendor item list (VITMLISTER) files are analyzed; other
          known reports are named but not analyzed yet.
        </T>
      }
      actions={
        results.length > 0 ? (
          <div className="flex gap-2">
            <DailyReportsDownloadControls
              summary={summary}
              isAnalyzing={isAnalyzing}
            />
            <Button variant="outline" onClick={clear} disabled={isAnalyzing}>
              <TrashIcon className="h-4 w-4" />
              <T context="button that clears all dropped daily report files">
                Clear Files
              </T>
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 pt-4">
        <FileDropzone
          multiple
          accept={DAILY_REPORT_ACCEPT}
          disabled={isAnalyzing}
          prompt={
            <T context="instruction inside the daily reports drag-and-drop area">
              Drag and drop .xls, .xlsx, or .xlsb daily reports here. You can
              drop several files at once.
            </T>
          }
          validate={(file) =>
            Promise.resolve(validateDailyReportFile(file, gt))
          }
          onFilesAccepted={(files) => {
            void addFiles(files);
          }}
        />
        <FileResultList
          results={results}
          pending={pending}
          onRemove={removeFile}
        />
        <AnalysisSummary summary={summary} />
        {hasAnalyzed ? (
          <RdStockUnmatchedTable rows={summary.rdStockUnmatched} />
        ) : null}
        {hasAnalyzed ? (
          <RdStockMappedTable rows={summary.rdStockMapped} />
        ) : null}
        {hasAnalyzed ? (
          <SupersededUnmatchedTable rows={summary.supersededUnmatched} />
        ) : null}
        {hasAnalyzed ? (
          <SupersededMappedTable rows={summary.supersededMapped} />
        ) : null}
        {hasAnalyzed ? <WebNoPriceTable rows={summary.webNoPrice} /> : null}
        {hasAnalyzed ? <PotfUpcTable rows={summary.potfUpc} /> : null}
        {hasAnalyzed ? (
          <VendorItemListTable rows={summary.vendorItemList} />
        ) : null}
        {hasAnalyzed && summary.flagged.length > 0 ? (
          <FlaggedRowsTable rows={summary.flagged} />
        ) : null}
        {hasAnalyzed &&
        hasAnalyzedSuspended &&
        summary.flaggedCount === 0 &&
        summary.unreadableCount === 0 ? (
          <AllClearPanel rowsScanned={summary.rowsScanned} />
        ) : null}
      </div>
    </Page>
  );
}
